import { BorderColor, Delete, Visibility, Settings, RestoreFromTrash } from '@mui/icons-material';
import { Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Link, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';
import axios from 'axios';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSnackbarAlert from '../../../utils/snackbarHandlers';

const ShopListRenderer: React.FC<any> = () => {
    //@ts-ignore
    const config = window.config;
    const apiBaseURL = config?.server || 'http://18.217.95.31:8080';
    const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();
    const { t } = useTranslation('translation');
    const [data, setData] = React.useState<Array<any>>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [totalCount, setTotalCount] = React.useState<number>(0);
    const [refreshTable, setRefreshTable] = React.useState<number>(0);
    // deleting a shop only deactivates it, so the list has to be able to show
    // the deactivated ones too - otherwise a deleted shop is unreachable forever
    const [statusFilter, setStatusFilter] = React.useState<string>("true");
    const [confirmAction, setConfirmAction] = React.useState<{ type: 'delete' | 'restore'; id: any } | null>(null);
    const navigate = useNavigate();
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 10,
    });

    const payload = {
        pageSize: paginationModel.pageSize,
        pageNumber: paginationModel.page + 1,
        isActive: [statusFilter],
    };

    React.useEffect(() => {
        setLoading(true);
        axios.post(`${apiBaseURL}/api/shop/fetchShops`, payload)
            .then((res: any) => {
                setData(res?.data?.shops ?? [])
                setTotalCount(res?.data?.totalRecords ?? 0)
            })
            .catch((error: any) => {
                console.log("err", error)
                setData([])
                setTotalCount(0)
                showErrorMessage(error?.response?.data?.message || t("somethingWentWrong"))
            })
            .finally(() => setLoading(false));
    }, [paginationModel, refreshTable, statusFilter]);

    //deleting sets isActive false and restoring sets it back to true; the shop
    //record itself is always kept, because its sales history depends on it
    const handleStatusChange = React.useCallback((id: any, isActive: boolean) => {
        axios.put(`${apiBaseURL}/api/shop/updateShop?id=${id}`, { isActive, lastUpdatedBy: "Bhavesh" })
            .then((res: any) => {
                setConfirmAction(null);
                setRefreshTable(Math.random());
                showSuccessMessage(isActive ? t("restoredShopSuccessMessage") : t("deletedShopSuccessMessage"));
            })
            .catch((error: any) => {
                showErrorMessage(error?.response?.data?.message || t("somethingWentWrong"))
            })
    }, []);

    const handleCloseDialogbox = () => {
        setConfirmAction(null);
    };

    const handleStatusFilterChange = (_event: React.MouseEvent<HTMLElement>, value: string | null) => {
        //null means the active button was clicked again - keep the current filter
        if (value === null) return;
        setStatusFilter(value);
        setPaginationModel((previous) => ({ ...previous, page: 0 }));
    };

    const fetchSubscriptionDetails = async (id: any) => {
        axios.get(`${apiBaseURL}/api/subscription/fetchShopSubscriptionDetails?id=${id}`).then(async (response: any) => {
            await localStorage.setItem("subscription", JSON.stringify(response?.data?.subscription));
            navigate(`/admin/setting/${id}`);
        });
    }

    const columns: GridColDef[] = [
        {
            field: 'fullName',
            headerName: 'Shop name',
            width: 200,
            align: "center",
            headerAlign: "center",
            flex: 1,
        },
        {
            field: 'address',
            headerName: 'Address',
            width: 200,
            align: "center",
            headerAlign: "center",
            flex: 1,
        },
        {
            field: 'email',
            headerName: 'Email',
            width: 200,
            align: "center",
            headerAlign: "center",
            flex: 1,
        },
        {
            field: 'createdAt',
            headerName: 'Date',
            width: 200,
            align: "center",
            headerAlign: "center",
            flex: 1,
            valueFormatter: (params: any) => {
                const creationDate = params.value;
                // return (
                //     (creationDate?.split("T")[0] as string) +
                //     " " +
                //     (creationDate?.split("T")[1] as string)?.slice(0, 8)
                // );
                const date = creationDate?.split("T")[0];
                const splittedDate = date.split("-");
                const formattedDate = `${splittedDate[1]}-${splittedDate[2]}-${splittedDate[0]}`;
                return formattedDate;
            },
        },
        {
            field: 'phone',
            headerName: 'Phone',
            width: 200,
            align: "center",
            headerAlign: "center",
            flex: 1,
        },
        {
            field: 'paymentStatus',
            headerName: 'Payment Status',
            width: 200,
            align: "center",
            headerAlign: "center",
            flex: 1,
            renderCell: (params: GridCellParams) => {
                return (
                    <div className='flex'>
                        <div>{params?.value === "PAID" ? "Paid" : "Un-Paid"}</div>
                        <div className={`${params?.value === "PAID" ? "active-shop" : "inactive-shop"} ms-2 rounded-[50%] text-white w-[20px]`}></div>
                    </div>
                );
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            width: 200,
            renderCell: (params: GridCellParams) => {
                return (
                    <ButtonGroup size="small" color="inherit" aria-label="group action buttons">
                        <Tooltip title="View">
                            <Button
                                onClick={(e) => navigate(`/admin/viewShop/${params?.id}`)}
                            >
                                <Visibility fontSize="small" />
                            </Button>
                        </Tooltip>
                        <Tooltip title="Edit">
                            <Button
                                onClick={(e) => navigate(`/admin/editShop/${params?.id}`)}
                            >
                                <BorderColor fontSize="small" />
                            </Button>
                        </Tooltip>
                        {params.row.isActive
                            ? (
                                <Tooltip title="Delete">
                                    <Button
                                        onClick={() => setConfirmAction({ type: 'delete', id: params.row.uId })}
                                    >
                                        <Delete fontSize="small" />
                                    </Button>
                                </Tooltip>
                            )
                            : (
                                <Tooltip title={t("restoreShop")}>
                                    <Button
                                        onClick={() => setConfirmAction({ type: 'restore', id: params.row.uId })}
                                    >
                                        <RestoreFromTrash fontSize="small" />
                                    </Button>
                                </Tooltip>
                            )}
                        <Tooltip title="Setting">
                            <Button
                                onClick={(e) => fetchSubscriptionDetails(params?.id)}
                            >
                                <Settings fontSize="small" />
                            </Button>
                        </Tooltip>
                    </ButtonGroup>
                );
            },
            flex: 1,
        },
    ];



    return (
        <>
            <Dialog
                open={confirmAction !== null}
                onClose={handleCloseDialogbox}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {confirmAction?.type === 'restore' ? t('restoreShop') : t('deleteShop')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {confirmAction?.type === 'restore' ? t('restoreShopConfirmation') : t('deleteShopConfirmation')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialogbox}>{t('disagree')}</Button>
                    <Button
                        onClick={() => confirmAction && handleStatusChange(confirmAction.id, confirmAction.type === 'restore')}
                        autoFocus
                    >
                        {t('agree')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Grid className="w-full flex justify-end">
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    color="primary"
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    aria-label={t('shopStatusFilter')}
                >
                    <ToggleButton value="true">{t('activeShops')}</ToggleButton>
                    <ToggleButton value="false">{t('deletedShops')}</ToggleButton>
                </ToggleButtonGroup>
            </Grid>
            <Grid className="w-full" style={{
                marginTop: "1%",
                height: "630px",
            }}>
                <DataGrid
                    rows={data}
                    loading={loading}
                    columns={columns}
                    rowCount={totalCount}
                    pageSizeOptions={[10, 20, 50, 100]}
                    pagination
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    getRowId={(row) => row.uId}
                    disableColumnSelector
                    disableRowSelectionOnClick
                    sx={{
                        '& .MuiDataGrid-main': {
                            borderColor: "#DAA520",
                        },
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: "#F5F5E6",

                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,

                        },
                        '& .MuiDataGrid-row.Mui-odd': {
                            backgroundColor: "#F5F5E6",
                        }
                        // maxWidth: "fit-content",
                    }}
                    getRowClassName={(params) => {
                        return params.id as number % 2 === 0 ? 'Mui-even' : 'Mui-odd'
                    }
                    }
                    columnBuffer={0}
                // autoHeight

                />
            </Grid>
        </>
    );
}
export default ShopListRenderer;